// This hook drives the filter side of the search experience: every facet
// toggle, sort change, or active-filter clear lands here and updates the URL
// via history.pushState. Filters are deliberately client-driven because their
// URL cardinality is high (long-tail combinations cache poorly on Cloudflare)
// and users often click several filters in quick succession — Next.js prefetch
// can't keep up, and a full server re-render per toggle would feel sluggish.
//
// Pagination is the opposite case and uses real Next.js navigation instead;
// see src/app/search/[[...slug]]/containers/Pagination.tsx for the rationale.
// The asymmetry is intentional, not an inconsistency to clean up.
//
// A consequence of pushState here: generateMetadata does NOT re-run on filter
// toggles, so document.title / canonical / og:url have to be synced manually
// in SearchContainer's useEffect.

import { useCallback } from 'react';
import useVehicleFacets from '@//data/search/vehicles/useVehicleFacets';
import { cleanParams, facetParamsToPath, pathToFacetParams } from '@//utils/vehicles-search/utils';

interface UpdateUrlOptions {
  replace?: boolean;
  scrollTop?: boolean;
}

const useUpdateVehiclesSearchUrl = (rootUrl: string) => {
  const facetsQuery = useVehicleFacets();
  const facets = facetsQuery.data;

  const updateUrl = useCallback(
    (rawParams: URLSearchParams, options?: UpdateUrlOptions) => {
      // 1) Clean out only valid facet‐params from the raw URLSearchParams
      const clean = cleanParams(rawParams, facets?.facets!);

      // 2) Build your slug
      const slug = facetParamsToPath(clean, facets?.facets!);

      // 3) Figure out which params actually came from facets…
      const used = slug ? pathToFacetParams(slug, facets?.facets!) : new URLSearchParams();

      // 4) …and collect the leftovers
      const leftover = new URLSearchParams(clean);
      used.forEach((_v, k) => leftover.delete(k));

      // 5) Build final URL
      let newUrl = rootUrl;
      if (slug) {
        newUrl += `/${slug}`;
        if (leftover.toString()) newUrl += `?${leftover.toString()}`;
      } else if (clean.toString()) {
        newUrl += `?${clean.toString()}`;
      }

      // 6) Push or replace history
      if (options?.replace) {
        window.history.replaceState(null, '', newUrl);
      } else {
        window.history.pushState(null, '', newUrl);
      }

      // 7) Optionally scroll
      if (options?.scrollTop) window.scrollTo({ top: 0 });
    },
    [rootUrl, facets?.facets],
  );

  return updateUrl;
};

export default useUpdateVehiclesSearchUrl;
